/**
 * Team phone check-in: /join/:pin (and /join to type a PIN).
 *
 * A captain scans the QR code on the projector. No account is needed. The
 * captain picks a returning team from the venue's list (fuzzy search) or
 * types a new name, enters the player count, and submits. The phone keeps a
 * device token (utils/deviceToken.ts), so next week it offers "Welcome
 * back". The host sees the check-in live and can rename, merge, or reject.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  TextField,
} from '@radix-ui/themes';
import { matchTeams } from '../Scoring/teamMatch';
import {
  fetchJoin,
  fetchVenueTeams,
  joinErrorMessage,
  submitCheckin,
  type JoinEvent,
  type JoinMe,
  type JoinReturning,
  type VenueTeam,
} from '../../utils/joinApi';
import { useRoomSignals } from '../../utils/useRealtime';
import PhoneAnswers, { type LivePlayState } from './PhoneAnswers';
import './JoinPage.css';

type Step = 'start' | 'pick' | 'details';

const MAX_PLAYERS = 30;

export default function JoinPage() {
  const { pin } = useParams<{ pin: string }>();
  if (!pin) return <PinEntry />;
  return <JoinFlow pin={pin} />;
}

/** /join with no PIN: type the 6-digit PIN from the projector. */
function PinEntry() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const valid = /^\d{6}$/.test(value);
  return (
    <div className="join-page">
      <Card size="3" className="join-card">
        <Flex direction="column" gap="4">
          <Heading size="6">Join tonight&rsquo;s trivia</Heading>
          <Text color="gray">Type the 6-digit PIN from the screen.</Text>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (valid) navigate(`/join/${value}`);
            }}
          >
            <Flex direction="column" gap="3">
              <TextField.Root
                size="3"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                aria-label="Game night PIN"
              />
              <Button size="3" type="submit" disabled={!valid}>
                Continue
              </Button>
            </Flex>
          </form>
        </Flex>
      </Card>
    </div>
  );
}

function JoinFlow({ pin }: { pin: string }) {
  const [event, setEvent] = useState<JoinEvent | null>(null);
  const [me, setMe] = useState<JoinMe | null>(null);
  const [returning, setReturning] = useState<JoinReturning | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [removedNotice, setRemovedNotice] = useState(false);

  const [step, setStep] = useState<Step>('start');
  const [editing, setEditing] = useState(false);
  const [teams, setTeams] = useState<VenueTeam[] | null>(null);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<VenueTeam | null>(null);
  const [name, setName] = useState('');
  const [players, setPlayers] = useState(4);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchJoin(pin);
      setEvent(data.event);
      setReturning(data.returning);
      setMe((prev) => {
        // The host rejected or merged away this phone's team.
        if (prev && !data.me) setRemovedNotice(true);
        return data.me;
      });
      setLoadError(null);
    } catch (err) {
      setLoadError(joinErrorMessage(err, 'Could not load this game night.'));
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => {
    load();
  }, [load]);

  // One socket for the page: team changes, and (on phone-answer nights)
  // the host's position and round locks.
  const [live, setLive] = useState<LivePlayState | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = useCallback(() => {
    load();
    setReloadKey((k) => k + 1);
  }, [load]);
  useRoomSignals(
    { event: 'join-play', pin },
    {
      'teams-changed': refresh,
      'play-state': (payload) => setLive(payload as LivePlayState),
    },
    refresh,
  );

  const openPicker = async () => {
    setStep('pick');
    if (teams) return;
    try {
      setTeams(await fetchVenueTeams(pin));
    } catch (err) {
      setSubmitError(joinErrorMessage(err, 'Could not load the team list.'));
      setTeams([]);
    }
  };

  const startDetails = (team: VenueTeam | null, presetName?: string, presetPlayers?: number) => {
    setPicked(team);
    setName(presetName ?? team?.name ?? '');
    if (presetPlayers) setPlayers(Math.min(MAX_PLAYERS, Math.max(1, presetPlayers)));
    setSubmitError(null);
    setStep('details');
  };

  const matches = useMemo(() => {
    if (!teams) return [];
    if (!query.trim()) return teams.map((team) => ({ team }));
    return matchTeams(teams, query);
  }, [teams, query]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await submitCheckin(pin, {
        name: trimmed,
        playerCount: players,
        canonicalTeamId: picked?.id ?? null,
      });
      setMe(data.me);
      setRemovedNotice(false);
      setEditing(false);
    } catch (err) {
      setSubmitError(joinErrorMessage(err, 'Check-in failed. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="join-page">
        <Flex align="center" justify="center" gap="2" p="6">
          <Spinner /> <Text>Loading…</Text>
        </Flex>
      </div>
    );
  }

  if (loadError || !event) {
    return (
      <div className="join-page">
        <Card size="3" className="join-card">
          <Flex direction="column" gap="3">
            <Heading size="5">Game night not found</Heading>
            <Text color="gray">{loadError || 'Check the PIN on the screen.'}</Text>
            <Button variant="soft" onClick={() => { setLoading(true); load(); }}>
              Try again
            </Button>
          </Flex>
        </Card>
      </div>
    );
  }

  const header = (
    <Box mb="4">
      <Text size="2" color="gray" as="p">{event.venueName}</Text>
      <Heading size="6">{event.title}</Heading>
    </Box>
  );

  // ── Checked in ──
  if (me && !editing) {
    return (
      <div className="join-page">
        {header}
        <Card size="3" className="join-card">
          <Flex direction="column" gap="3">
            <Badge color="green" size="2" className="join-badge">Checked in</Badge>
            <Heading size="7" className="join-team-name">{me.name}</Heading>
            <Text color="gray">
              {me.playerCount} {me.playerCount === 1 ? 'player' : 'players'}
            </Text>
            <Text size="2" color="gray">
              {event.answerMode && event.answerMode !== 'PAPER'
                ? 'Answer below. Keep this page open.'
                : 'Keep this page open. Tell the host if anything is wrong.'}
            </Text>
            {event.status === 'LIVE' && event.checkInEnabled && (
              <Button
                variant="soft"
                color="gray"
                onClick={() => {
                  setPicked(me.canonicalTeamId ? { id: me.canonicalTeamId, name: me.name, checkedIn: true } : null);
                  setName(me.name);
                  setPlayers(me.playerCount);
                  setEditing(true);
                  setStep('details');
                }}
              >
                Change name or player count
              </Button>
            )}
          </Flex>
        </Card>
        {event.answerMode && event.answerMode !== 'PAPER' && event.status === 'LIVE' && (
          <PhoneAnswers pin={pin} me={me} live={live} reloadKey={reloadKey} />
        )}
      </div>
    );
  }

  if (event.status !== 'LIVE') {
    return (
      <div className="join-page">
        {header}
        <Callout.Root color="gray">
          <Callout.Text>
            {event.status === 'DRAFT'
              ? 'This game night has not started yet. The host starts it from the Game Night hub.'
              : 'This game night is over.'}
          </Callout.Text>
        </Callout.Root>
      </div>
    );
  }

  if (!event.checkInEnabled) {
    return (
      <div className="join-page">
        {header}
        <Callout.Root>
          <Callout.Text>Phone check-in is off tonight. Give your team name to the host.</Callout.Text>
        </Callout.Root>
      </div>
    );
  }

  return (
    <div className="join-page">
      {header}
      {removedNotice && (
        <Callout.Root color="amber" mb="3">
          <Callout.Text>The host removed your check-in. Check in again or talk to the host.</Callout.Text>
        </Callout.Root>
      )}

      {step === 'start' && (
        <Flex direction="column" gap="3">
          {returning && (
            <Card size="3" className="join-card join-welcome">
              <Flex direction="column" gap="3">
                <Text size="2" color="gray">Welcome back,</Text>
                <Heading size="6">{returning.name}</Heading>
                <Button
                  size="3"
                  onClick={() =>
                    startDetails(
                      returning.canonicalTeamId
                        ? { id: returning.canonicalTeamId, name: returning.canonicalName || returning.name, checkedIn: false }
                        : null,
                      returning.name,
                      returning.playerCount,
                    )
                  }
                >
                  That is us
                </Button>
              </Flex>
            </Card>
          )}
          <Heading size="4" mt="2">{returning ? 'A different team?' : 'Check in your team'}</Heading>
          <Button size="4" variant={returning ? 'soft' : 'solid'} onClick={openPicker}>
            Returning team
          </Button>
          <Button size="4" variant="soft" onClick={() => startDetails(null, '')}>
            New team
          </Button>
        </Flex>
      )}

      {step === 'pick' && (
        <Flex direction="column" gap="3">
          <Flex align="center" gap="2">
            <IconButton variant="ghost" aria-label="Back" onClick={() => setStep('start')}>&larr;</IconButton>
            <Heading size="4">Find your team</Heading>
          </Flex>
          <TextField.Root
            size="3"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Start typing your team name"
            autoComplete="off"
            autoFocus
            aria-label="Search teams"
          />
          {teams === null ? (
            <Flex gap="2" align="center"><Spinner /> <Text>Loading teams…</Text></Flex>
          ) : (
            <div className="join-team-list" role="list">
              {matches.slice(0, 50).map(({ team }) => (
                <button
                  key={team.id}
                  type="button"
                  role="listitem"
                  className="join-team-row"
                  onClick={() => startDetails(team)}
                >
                  <span>{team.name}</span>
                  {team.checkedIn && <Badge color="amber">here tonight</Badge>}
                </button>
              ))}
              {matches.length === 0 && (
                <Text color="gray" size="2">No match. You can check in as a new team.</Text>
              )}
            </div>
          )}
          <Button size="3" variant="soft" onClick={() => startDetails(null, query.trim())}>
            We are a new team
          </Button>
        </Flex>
      )}

      {step === 'details' && (
        <form onSubmit={submit}>
          <Flex direction="column" gap="4">
            <Flex align="center" gap="2">
              <IconButton
                type="button"
                variant="ghost"
                aria-label="Back"
                onClick={() => (editing ? setEditing(false) : setStep(picked ? 'pick' : 'start'))}
              >
                &larr;
              </IconButton>
              <Heading size="4">{editing ? 'Update your check-in' : picked ? 'Returning team' : 'New team'}</Heading>
            </Flex>
            {picked && (
              <Text size="2" color="gray">
                {editing ? (
                  'Your team history stays linked.'
                ) : (
                  <>
                    Linked to <strong>{picked.name}</strong>. You can use a different name tonight.
                    Your team history stays linked.
                  </>
                )}
              </Text>
            )}
            <label className="join-field">
              <Text size="2" weight="bold">Team name for tonight</Text>
              <TextField.Root
                size="3"
                value={name}
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
                placeholder="Team name"
                autoComplete="off"
                required
              />
            </label>
            <div className="join-field">
              <Text size="2" weight="bold">How many players?</Text>
              <Flex align="center" gap="3">
                <IconButton
                  type="button"
                  size="3"
                  variant="soft"
                  aria-label="Fewer players"
                  disabled={players <= 1}
                  onClick={() => setPlayers((n) => Math.max(1, n - 1))}
                >
                  &minus;
                </IconButton>
                <Text size="7" weight="bold" className="join-count" aria-live="polite">{players}</Text>
                <IconButton
                  type="button"
                  size="3"
                  variant="soft"
                  aria-label="More players"
                  disabled={players >= MAX_PLAYERS}
                  onClick={() => setPlayers((n) => Math.min(MAX_PLAYERS, n + 1))}
                >
                  +
                </IconButton>
              </Flex>
            </div>
            {submitError && (
              <Callout.Root color="red"><Callout.Text>{submitError}</Callout.Text></Callout.Root>
            )}
            <Button size="4" type="submit" disabled={!name.trim() || submitting}>
              {submitting ? 'Checking in…' : editing ? 'Save' : 'Check in'}
            </Button>
          </Flex>
        </form>
      )}
    </div>
  );
}
